import { useCallback } from 'react';
import teamsService from '../../services/teamsService';
import { getTeamsCache, saveTeamsCache, getTeamDetailCache, saveTeamDetailCache } from './teamsCache';

/** Loading teams, members, invitations, workspaces, boards and activity. */
export function useTeamsLoaders({
  onTeamsChange,
  setLoading,
  setError,
  setUniqueMembers,
  setMembers,
  setMembersLoading,
  setInvitations,
  setInvLoading,
  setTeamWs,
  setWsLoading,
  setTeamBoards,
  setBoardsLoading,
  setActivities,
  setActLoading,
  actFilter,
  activeTeam,
  isAdmin,
}) {
  // ── loaders ───────────────────────────────────────────────────────────────────
  const loadTeams = useCallback(async () => {
    setError(null);

    // Show cached data instantly so there's no content jump
    const cached = getTeamsCache();
    if (cached) {
      onTeamsChange(cached.teams || []);
      setUniqueMembers(cached.uniqueMembers || 0);
      setLoading(false);
    }

    // Fetch fresh data in the background
    try {
      const res = await teamsService.getTeams();
      const arr = Array.isArray(res) ? res : (res?.data??[]);
      onTeamsChange(arr);
      setUniqueMembers(res?.meta?.unique_members??0);
      saveTeamsCache(arr, res?.meta?.unique_members??0);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [onTeamsChange, setError, setLoading, setUniqueMembers]);

  const loadMembers = useCallback(async () => {
    if (!activeTeam?.id) { setMembers([]); return; }
    const cached = getTeamDetailCache(activeTeam.id);
    if (cached?.members) { setMembers(cached.members); } else { setMembersLoading(true); }
    try {
      const d = await teamsService.getTeamMembers(activeTeam.id) || [];
      setMembers(d);
      saveTeamDetailCache(activeTeam.id, { members: d });
    } catch { setMembers([]); }
    finally { setMembersLoading(false); }
  }, [activeTeam?.id, setMembers, setMembersLoading]);

  const loadInvitations = useCallback(async () => {
    if (!activeTeam?.id||!isAdmin(activeTeam)) { setInvitations([]); return; }
    const cached = getTeamDetailCache(activeTeam.id);
    if (cached?.invitations) { setInvitations(cached.invitations); } else { setInvLoading(true); }
    try {
      const d = await teamsService.getTeamInvitations(activeTeam.id);
      const arr = Array.isArray(d) ? d : [];
      setInvitations(arr);
      saveTeamDetailCache(activeTeam.id, { invitations: arr });
    } catch { setInvitations([]); }
    finally { setInvLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id + role are everything isAdmin reads; isAdmin is redeclared each render, and this callback gates the detail-load effect below, so an unstable identity would refetch the team on every render
  }, [activeTeam?.id, activeTeam?.role]);

  const loadWs = useCallback(async () => {
    if (!activeTeam?.id) { setTeamWs([]); return; }
    const cached = getTeamDetailCache(activeTeam.id);
    if (cached?.workspaces) { setTeamWs(cached.workspaces); } else { setWsLoading(true); }
    try {
      const d = await teamsService.getTeamWorkspaces(activeTeam.id);
      const arr = Array.isArray(d) ? d : [];
      setTeamWs(arr);
      saveTeamDetailCache(activeTeam.id, { workspaces: arr });
    } catch { setTeamWs([]); }
    finally { setWsLoading(false); }
  }, [activeTeam?.id, setTeamWs, setWsLoading]);

  const loadBoards = useCallback(async () => {
    if (!activeTeam?.id) { setTeamBoards([]); return; }
    const cached = getTeamDetailCache(activeTeam.id);
    if (cached?.boards) { setTeamBoards(cached.boards); } else { setBoardsLoading(true); }
    try {
      const d = await teamsService.getTeamBoards(activeTeam.id);
      const arr = Array.isArray(d) ? d : [];
      setTeamBoards(arr);
      saveTeamDetailCache(activeTeam.id, { boards: arr });
    } catch { setTeamBoards([]); }
    finally { setBoardsLoading(false); }
  }, [activeTeam?.id, setBoardsLoading, setTeamBoards]);

  const loadActivity = useCallback(async () => {
    if (!activeTeam?.id) { setActivities([]); return; }
    const cached = getTeamDetailCache(activeTeam.id);
    if (!actFilter && cached?.activities) { setActivities(cached.activities); } else { setActLoading(true); }
    try {
      const d = await teamsService.getTeamActivity(activeTeam.id, actFilter);
      const arr = Array.isArray(d) ? d : [];
      setActivities(arr);
      if (!actFilter) saveTeamDetailCache(activeTeam.id, { activities: arr });
    } catch { setActivities([]); }
    finally { setActLoading(false); }
  }, [activeTeam?.id, actFilter, setActLoading, setActivities]);

  return { loadTeams, loadMembers, loadInvitations, loadWs, loadBoards, loadActivity };
}
